// frontend/src/pages/News.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublicPostsApi } from '../api/content.api';
import { POST_TYPE_LABELS, POST_STATUS_LABELS } from '../utils/constants';
import { toJalaliFull } from '../utils/jalali';
import Input from '../components/common/Input';
import Pagination from '../components/common/Pagination';
import Card from '../components/common/Card';
import StatusBadge from '../components/common/StatusBadge';
import Icon from '../components/common/Icon';
import AnimatedCounter from '../components/common/AnimatedCounter';

const TYPE_ICONS = {
  ALL: 'news',
  NEWS: 'news',
  EVENT: 'calendar',
  BLOG: 'document',
  ARTICLE: 'document',
  ANNOUNCEMENT: 'bell',
};

const TYPE_COLORS = {
  NEWS: 'bg-blue-50 text-blue-700 border-blue-200',
  EVENT: 'bg-amber-50 text-amber-700 border-amber-200',
  BLOG: 'bg-violet-50 text-violet-700 border-violet-200',
  ARTICLE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ANNOUNCEMENT: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function News() {
  const [posts, setPosts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    totalPages: 1,
  });

  const [loading, setLoading] = useState(true);
  const [visiblePosts, setVisiblePosts] = useState(6);

  const contentTypes = useMemo(() => {
    return [
      {
        key: '',
        label: 'همه مطالب',
        icon: TYPE_ICONS.ALL,
        count: pagination.total,
      },
      ...Object.entries(POST_TYPE_LABELS).map(([key, label]) => ({
        key,
        label,
        icon: TYPE_ICONS[key] || 'document',
        count: posts.filter(p => p.type === key).length,
      })),
    ];
  }, [posts, pagination.total]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const clean = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '')
    );

    getPublicPostsApi({
      ...clean,
      page: pagination.page,
      limit: pagination.limit,
    })
      .then(({ data }) => {
        if (!mounted) return;
        setPosts(data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data.meta?.total || 0,
          totalPages: data.meta?.totalPages || 1,
        }));
      })
      .catch(() => {
        if (!mounted) return;
        setPosts([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
          totalPages: 1,
        }));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [filters, pagination.page, pagination.limit]);

  const handleTypeChange = (type) => {
    setFilters((prev) => ({ ...prev, type }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    setVisiblePosts(6);
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    setVisiblePosts(6);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      setVisiblePosts(6);
    }
  };

  const handleLoadMore = () => {
    setVisiblePosts((prev) => Math.min(prev + 6, posts.length));
  };

  const activeTypeLabel =
    filters.type === ''
      ? 'همه مطالب'
      : POST_TYPE_LABELS[filters.type] || filters.type;

  const displayedPosts = posts.slice(0, visiblePosts);

  return (
    <section dir="rtl" className="min-h-screen py-16 animate-fade-in">
      <div className="container-page">
        {/* =========================================================
            HEADER با تصویر پس‌زمینه
        ========================================================= */}
        <div className="relative mb-12 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-8 text-white shadow-2xl md:p-12">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-lime-500/10 blur-3xl" />

          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-emerald-300">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                مرکز محتوای هیئت فوتبال
              </div>
              <h1 className="text-3xl font-black tracking-tight md:text-4xl lg:text-5xl">
                اخبار و مطالب
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-white/60 md:text-base">
                آخرین اخبار، رویدادها، مقالات و اطلاعیه‌های هیئت فوتبال شهرستان پاوه را از این بخش دنبال کنید.
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 backdrop-blur-sm">
                <div className="text-xs font-bold text-white/40">تعداد مطالب</div>
                <div className="mt-1 text-2xl font-black text-white">
                  {loading ? (
                    <span className="inline-block h-7 w-12 animate-pulse rounded bg-white/10" />
                  ) : (
                    <AnimatedCounter value={pagination.total} duration={1500} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* جستجو */}
          <div className="relative mt-6 md:mt-8">
            <div className="relative max-w-md">
              <Input
                placeholder="جستجو در مطالب..."
                value={filters.search}
                onChange={handleSearchChange}
                className="h-12 w-full rounded-2xl border-white/10 bg-white/10 pr-4 text-white placeholder:text-white/40 backdrop-blur-sm transition-all focus:border-emerald-400 focus:bg-white/20"
              />
              <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/30">
                <Icon name="search" className="h-5 w-5" />
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================
            MAIN LAYOUT
        ========================================================= */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
          {/* =========================================================
              SIDEBAR
          ========================================================= */}
          <aside className="lg:sticky lg:top-6 lg:h-fit">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              {/* Sidebar Header */}
              <div className="border-b border-slate-100 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur">
                    <Icon name="news" className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-black">دسته‌ بندی مطالب</div>
                    <div className="mt-1 text-[11px] text-white/60">انتخاب بخش موردنظر</div>
                  </div>
                </div>
              </div>

              {/* Sidebar Items */}
              <nav className="p-3">
                <div className="space-y-1">
                  {contentTypes.map((item) => {
                    const active = filters.type === item.key;
                    return (
                      <button
                        key={item.key || 'all'}
                        type="button"
                        onClick={() => handleTypeChange(item.key)}
                        className={`
                          group relative flex w-full items-center
                          gap-3 rounded-2xl px-4 py-3
                          text-right transition-all duration-300
                          ${
                            active
                              ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }
                        `}
                      >
                        {active && (
                          <span className="absolute right-0 top-1/2 h-7 w-1 -translate-y-1/2 rounded-l-full bg-emerald-500" />
                        )}

                        <span
                          className={`
                            flex h-10 w-10 shrink-0 items-center
                            justify-center rounded-xl transition-all
                            ${
                              active
                                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                                : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
                            }
                          `}
                        >
                          <Icon name={item.icon} className="h-5 w-5" />
                        </span>

                        <span className="flex-1">
                          <span className="block text-sm font-bold">{item.label}</span>
                          {active && (
                            <span className="mt-0.5 block text-[10px] font-medium text-emerald-500">
                              بخش فعال
                            </span>
                          )}
                        </span>

                        <span className="text-xs font-bold text-slate-400">
                          {item.count}
                        </span>

                        <Icon
                          name="chevronLeft"
                          className={`
                            h-4 w-4 transition-transform
                            ${
                              active
                                ? 'translate-x-0 text-emerald-500'
                                : 'translate-x-1 text-slate-300 group-hover:translate-x-0'
                            }
                          `}
                        />
                      </button>
                    );
                  })}
                </div>
              </nav>

              {/* Sidebar Footer */}
              <div className="border-t border-slate-100 p-4">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-xs font-bold text-slate-500">بخش انتخاب‌ شده</div>
                  <div className="mt-2 text-sm font-black text-slate-800">{activeTypeLabel}</div>
                  {!loading && (
                    <div className="mt-1 text-xs text-slate-400">{pagination.total} مطلب</div>
                  )}
                </div>
              </div>
            </div>
          </aside>

          {/* =========================================================
              CONTENT
          ========================================================= */}
          <main className="min-w-0">
            {/* Content Header */}
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-xs font-bold text-emerald-600">نمایش مطالب</div>
                <h2 className="mt-1 text-xl font-black text-slate-900">{activeTypeLabel}</h2>
              </div>
              {!loading && (
                <div className="rounded-full bg-slate-100 px-4 py-2 text-xs font-bold text-slate-500">
                  {pagination.total} مطلب
                </div>
              )}
            </div>

            {/* =========================================================
                LOADING
            ========================================================= */}
            {loading ? (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <NewsSkeleton key={item} />
                ))}
              </div>
            ) : posts.length === 0 ? (
              /* =========================================================
                  EMPTY
              ========================================================= */
              <div className="flex min-h-[420px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white px-6 text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-slate-100 text-slate-400">
                  <Icon name="news" className="h-9 w-9" />
                </div>
                <h3 className="mt-5 text-lg font-black text-slate-800">مطلبی پیدا نشد</h3>
                <p className="mt-2 max-w-md text-sm leading-7 text-slate-400">
                  در حال حاضر مطلبی در بخش «{activeTypeLabel}» منتشر نشده است.
                </p>
                {(filters.type || filters.search) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({ search: '', type: '' });
                      setPagination((prev) => ({ ...prev, page: 1 }));
                      setVisiblePosts(6);
                    }}
                    className="mt-5 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-600"
                  >
                    نمایش همه مطالب
                  </button>
                )}
              </div>
            ) : (
              /* =========================================================
                  POSTS
              ========================================================= */
              <>
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {displayedPosts.map((post, index) => (
                    <NewsCard key={post.id} post={post} index={index} />
                  ))}
                </div>

                {/* =========================================================
                    LOAD MORE / PAGINATION
                ========================================================= */}
                {posts.length > visiblePosts && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={handleLoadMore}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-600 transition-all hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600"
                    >
                      <Icon name="chevronDown" className="h-4 w-4" />
                      نمایش بیشتر
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                        {posts.length - visiblePosts} باقی‌ مانده
                      </span>
                    </button>
                  </div>
                )}

                {pagination.totalPages > 1 && (
                  <div className="mt-10">
                    <Pagination
                      page={pagination.page}
                      totalPages={pagination.totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// کامپوننت کارت خبر
// ============================================================
function NewsCard({ post, index }) {
  const typeColor = TYPE_COLORS[post.type] || 'bg-slate-50 text-slate-600 border-slate-200';

  return (
    <Card
      hover
      className="group flex flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white p-0 transition-all duration-500 hover:shadow-2xl"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* تصویر شاخص */}
      <div className="relative h-52 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-700 to-emerald-800">
        {post.coverImage ? (
          <>
            <img
              src={post.coverImage}
              alt={post.title}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Icon
              name={TYPE_ICONS[post.type] || 'news'}
              className="h-16 w-16 text-white/20"
            />
          </div>
        )}

        {/* برچسب نوع */}
        <div className="absolute right-3 top-3">
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold ${typeColor}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {POST_TYPE_LABELS[post.type] || post.type}
          </span>
        </div>

        {/* تعداد بازدید */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-black/30 px-3 py-1.5 text-[10px] font-bold text-white/70 backdrop-blur-sm">
          <Icon name="eye" className="h-3 w-3" />
          <span>{post.viewCount || 0}</span>
        </div>
      </div>

      {/* محتوا */}
      <div className="flex flex-1 flex-col p-5">
        {/* دسته‌بندی و تاریخ */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {post.category && (
            <span className="rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold text-blue-600">
              {post.category.title}
            </span>
          )}
          {post.publishDate && (
            <span className="text-[11px] text-slate-400">
              {toJalaliFull(post.publishDate)}
            </span>
          )}
        </div>

        {/* عنوان */}
        <h3 className="line-clamp-2 text-lg font-black leading-8 text-text-dark transition-colors group-hover:text-emerald-600">
          {post.title}
        </h3>

        {/* خلاصه */}
        <p className="mt-3 line-clamp-3 flex-1 text-sm leading-7 text-slate-500">
          {post.summary || post.content?.substring(0, 150) || 'بدون توضیحات'}...
        </p>

        {/* پایین کارت */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <Icon name="userGroup" className="h-4 w-4" />
              </div>
              <span className="truncate text-xs font-bold text-slate-500">
                {post.author?.fullName || 'ناشناس'}
              </span>
            </div>

            <Link
              to={`/news/${post.id}`}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-600 transition-all hover:bg-emerald-500 hover:text-white"
            >
              ادامه مطلب
              <Icon name="arrowLeft" className="h-3.5 w-3.5" />
            </Link>
          </div>

          {/* برچسب‌ها */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="rounded-full bg-slate-50 px-2.5 py-1 text-[10px] font-medium text-slate-500"
                >
                  #{tag.title}
                </span>
              ))}
              {post.tags.length > 3 && (
                <span className="rounded-full bg-slate-50 px-2.5 py-1 text-[10px] font-medium text-slate-400">
                  +{post.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ============================================================
// اسکلتون لودینگ
// ============================================================
function NewsSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white">
      <div className="h-52 animate-pulse bg-slate-100" />
      <div className="space-y-3 p-5">
        <div className="h-4 w-20 animate-pulse rounded bg-slate-100" />
        <div className="h-5 w-4/5 animate-pulse rounded bg-slate-100" />
        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-slate-100" />
        <div className="mt-4 flex items-center justify-between">
          <div className="h-8 w-8 animate-pulse rounded-full bg-slate-100" />
          <div className="h-8 w-20 animate-pulse rounded-xl bg-slate-100" />
        </div>
      </div>
    </div>
  );
}